/**
 * Backend URL resolution — runtime first, build-time fallback.
 *
 * Por que existe:
 *   Em produção, o frontend e o backend ficam no mesmo domínio
 *   (`nfcesys.com.br`) e o ingress roteia `/api/*` para o backend.
 *   Se baseamos a URL apenas em `REACT_APP_BACKEND_URL` (que é
 *   compilado no build), qualquer mudança de domínio ou redeploy
 *   "esquecido" deixa o app tentando acessar uma URL antiga.
 *
 *   Esta função decide a URL correta NO MOMENTO DA EXECUÇÃO:
 *   - Em produção (qualquer domínio diferente de localhost/preview)
 *     usa `window.location.origin`. Sempre acerta.
 *   - Em desenvolvimento ou preview (localhost / *.preview.emergent...)
 *     usa o valor de `REACT_APP_BACKEND_URL` (separação de portas/domínio).
 *
 * Resultado: você troca o domínio de produção, faz redeploy, e o app
 * continua funcionando sem precisar lembrar de atualizar variável.
 */

const buildTimeUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const looksLikeDevHost = (host) => {
  if (!host) return false;
  const h = host.toLowerCase();
  return (
    h === 'localhost' ||
    h.startsWith('localhost:') ||
    h.startsWith('127.') ||
    h.startsWith('192.168.') ||
    h.startsWith('10.') ||
    h.endsWith('.local')
  );
};

const looksLikePreviewHost = (host) => {
  if (!host) return false;
  return /\.preview\.emergent(agent)?\.com$/i.test(host) ||
    /\.emergent\.host$/i.test(host) === false && /preview/i.test(host);
};

export const getBackendUrl = () => {
  // SSR ou ambiente sem window: cai no build-time
  if (typeof window === 'undefined' || !window.location) {
    return buildTimeUrl;
  }
  const { origin, hostname } = window.location;

  // Dev local: backend roda em outra porta → precisa do REACT_APP_BACKEND_URL
  if (looksLikeDevHost(hostname)) {
    return buildTimeUrl || origin;
  }

  // Preview Emergent (frontend e backend em domínios distintos): respeita o env
  if (looksLikePreviewHost(hostname) && buildTimeUrl) {
    return buildTimeUrl;
  }

  // Produção (domínio próprio): backend está no mesmo origin via ingress
  return origin;
};

export const BACKEND_URL = getBackendUrl();
export default BACKEND_URL;
